import type { RunRecordResource } from "@/types_api/backend/transport/run_metadata_response"
import type { RunSummaryRowResource } from "@/types_api/backend/transport/run_rows_response"
import type { SimulationOutputResponse } from "@/types_api/backend/transport/simulation_output_response"
import type { RunRecord, RunStatus } from "@/types/api/RunRecord"
import { normalizeConversationSource } from "@/types/conversationSource"
import { asJsonFieldValueOrNull, type JsonObject } from "@/types/json"
import {
  toSubvaluesByTimestamp,
  type RunMetricOutput,
} from "@/types/RunMetricOutput"
import type { RunRowDetailLike, RunRowLike } from "@/types/RunRow"

import { parseSimulationOutput } from "../simulation-outputs/utils/parseSimulationOutput"

const RUN_STATUS_BY_BACKEND_VALUE: Record<string, RunStatus> = {
  CANCELLED: "CANCELLED",
  CANCELED: "CANCELLED",
  COMPLETED: "COMPLETED",
  COMPLETE: "COMPLETED",
  FAILED: "FAILED",
  "IN PROGRESS": "IN PROGRESS",
  "IN QUEUE": "IN QUEUE",
  PENDING: "IN QUEUE",
  QUEUED: "IN QUEUE",
  RUNNING: "IN PROGRESS",
}

type RunRecordResourceWithOptionalSummary = RunRecordResource & {
  run_summary?: string | null
}
type RunRowDetailResource = NonNullable<
  SimulationOutputResponse["simulation_output"]
>
type RunRowDetailResourceWithOptionalCallSid = RunRowDetailResource & {
  call_sid?: string | null
}

function toJsonObject(value: unknown): JsonObject {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as JsonObject
  }

  return {}
}

function normalizeMetricOutputs<TMetricOutput extends RunMetricOutput>(
  metricOutputs: RunRowDetailResource["metric_outputs"],
): Record<string, TMetricOutput> {
  const normalized: Record<string, TMetricOutput> = {}
  for (const [metricId, output] of Object.entries(metricOutputs ?? {})) {
    const metricOutput = output as TMetricOutput
    normalized[metricId] = {
      ...metricOutput,
      subvalues_by_timestamp: toSubvaluesByTimestamp(
        metricOutput?.subvalues_by_timestamp,
      ),
    } as TMetricOutput
  }

  return normalized
}

function getRunSummary(run: RunRecordResource): string | null {
  const runWithOptionalSummary = run as RunRecordResourceWithOptionalSummary

  return typeof runWithOptionalSummary.run_summary === "string"
    ? runWithOptionalSummary.run_summary
    : null
}

function isUploadedConversation({
  conversation_source: conversationSource,
  is_monitoring: isMonitoring,
  run_kind: runKind,
}: {
  conversation_source?: string | null
  is_monitoring?: boolean | null
  run_kind?: string | null
}): boolean {
  const source = normalizeConversationSource(conversationSource ?? runKind)
  return source ? source === "uploaded" : (isMonitoring ?? false)
}

export function normalizeRunStatus(
  status: string | null | undefined,
): RunStatus {
  const normalizedStatus = (status ?? "")
    .replace(/_/g, " ")
    .trim()
    .toUpperCase()

  return RUN_STATUS_BY_BACKEND_VALUE[normalizedStatus] ?? normalizedStatus
}

export function normalizeRunRecord(
  run: RunRecordResource | null | undefined,
): RunRecord | null {
  if (!run) {
    return null
  }

  return {
    ...run,
    config: toJsonObject(run.config),
    created_at: run.created_at ?? "",
    created_by: run.created_by ?? "",
    customer_metadata: toJsonObject(run.customer_metadata),
    is_public: run.is_public ?? false,
    last_updated_at: run.last_updated_at ?? "",
    model_metadata: toJsonObject(run.model_metadata),
    model_type: run.model_type ?? "",
    organization_id: run.organization_id,
    persona_id_id: run.persona_id ?? null,
    run_summary: getRunSummary(run),
    status: normalizeRunStatus(run.status),
    tags: run.tags ?? [],
    test_set_name: run.test_set_name ?? undefined,
    timing: toJsonObject(run.timing),
    is_monitoring: isUploadedConversation(run),
  }
}

/**
 * Detail/drawer rows fetched per-simulation via getSimulationOutput. These carry the
 * heavy fields (transcript, full metric payloads, integrations, config, ...), so the
 * input is the detail resource — passing a list (summary) row is a compile error.
 */
export function normalizeRunRowDetail<
  TMetricOutput extends RunMetricOutput = RunMetricOutput,
>(
  row: RunRowDetailResource & { status?: string | null },
): RunRowDetailLike<TMetricOutput> {
  const typedRow = row as RunRowDetailResourceWithOptionalCallSid
  const parsedRow = parseSimulationOutput({
    simulation_output_str: row.simulation_output_str ?? null,
    transcript: (row.transcript ??
      []) as RunRowDetailLike<TMetricOutput>["transcript"],
    transcript_type: row.transcript_type ?? "",
  })

  return {
    ...row,
    call_sid: typedRow.call_sid ?? null,
    config: toJsonObject(row.config),
    created_at: row.created_at ?? "",
    customer_metadata: toJsonObject(row.customer_metadata),
    error_message: row.error_message ?? null,
    error_status: row.error_status ?? null,
    expected_behaviors: row.expected_behaviors ?? [],
    expected_output_json: asJsonFieldValueOrNull(row.expected_output_json),
    explanations: toJsonObject(row.explanations),
    integrations: toJsonObject(row.integrations),
    integration_loaded: row.integration_loaded ?? false,
    input_str: row.input_str ?? null,
    is_monitoring: isUploadedConversation(row),
    metric_outputs: normalizeMetricOutputs<TMetricOutput>(row.metric_outputs),
    metrics: toJsonObject(row.metrics),
    model_type: row.model_type ?? null,
    notes: row.notes ?? null,
    persona_id: row.persona_id ?? null,
    simulation_output_loaded: row.simulation_output_loaded ?? false,
    simulation_output_str: row.simulation_output_str ?? null,
    simulation_s3_bucket: row.simulation_s3_bucket ?? null,
    simulation_s3_key: row.simulation_s3_key ?? null,
    status: row.status == null ? null : normalizeRunStatus(row.status),
    test_case_params: toJsonObject(row.test_case_params) as Record<
      string,
      string
    >,
    test_set_id: row.test_set_id ?? null,
    test_set_row_id: row.test_set_row_id ?? null,
    tool_calls: (row.tool_calls ?? []) as JsonObject[],
    transcript: parsedRow.transcript ?? [],
    transcript_type: parsedRow.transcript_type,
  }
}

/**
 * A run row as it arrives from the list endpoints (getRunRows / multi_run_rows / report
 * rows). The detail-only fields don't exist on this type — fetch the detail row via
 * getSimulationOutput (normalizeRunRowDetail) when you need a transcript or a metric's
 * full result payload.
 */
export type SummaryRunRow<
  TMetricOutput extends RunMetricOutput = RunMetricOutput,
> = RunRowLike<TMetricOutput>

/** Normalize a lightweight list/table row. Carries no detail-only fields. */
export function normalizeRunRowSummary<
  TMetricOutput extends RunMetricOutput = RunMetricOutput,
>(
  row: RunSummaryRowResource & { status?: string | null },
): SummaryRunRow<TMetricOutput> {
  return {
    agent_id: row.agent_id ?? null,
    created_at: row.created_at ?? "",
    customer_metadata: toJsonObject(row.customer_metadata),
    error_message: row.error_message ?? null,
    error_status: row.error_status ?? null,
    expected_behaviors: row.expected_behaviors ?? [],
    input_str: row.input_str ?? null,
    is_monitoring: isUploadedConversation(row),
    metric_outputs: (row.metric_outputs ?? {}) as Record<string, TMetricOutput>,
    model_type: row.model_type ?? null,
    mutation_id: row.mutation_id ?? null,
    mutation_name: row.mutation_name ?? null,
    notes: row.notes ?? null,
    persona_id: row.persona_id ?? null,
    run_id: row.run_id,
    simulation_end_time: row.simulation_end_time ?? null,
    simulation_output_id: row.simulation_output_id,
    simulation_s3_bucket: row.simulation_s3_bucket ?? null,
    simulation_s3_key: row.simulation_s3_key ?? null,
    // Carried so a row can say whether its simulation is still running: audio and
    // an end time are both written at completion.
    simulation_start_time: row.simulation_start_time ?? null,
    status: row.status == null ? null : normalizeRunStatus(row.status),
    test_case_params: toJsonObject(row.test_case_params) as Record<
      string,
      string
    >,
    test_set_id: row.test_set_id ?? null,
    test_set_row_id: row.test_set_row_id ?? null,
  }
}
